import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import {
  Figure,
  LoadMore,
  PersonScopeCard,
  TABLE_REGION,
  TD,
  TD_MUTED,
  TD_NUM,
  TH,
  TH_NUM,
  THEAD,
} from '@/pages/cvm/shared';
import { cvmPartyParams, fetchCvmParties } from '@/services/cvm';
import type { CvmDirectoryRow } from '@/services/types';

const PAGE_SIZE = 50;
const ENTITY_TYPES = ['person', 'company', 'unknown'] as const;

/**
 * The party directory: every name a source prints, ordered by name.
 *
 * Accused in a judgment, proponents of a settlement, appellants before the
 * CRSFN, names in the register's status events -- one row per canonical name,
 * persons and companies alike, exactly as published. Ordered by NAME and never
 * by a count, so the directory does not rank people by how often they appear.
 * A row opens the party's RECORD: what the documents say under that name, with
 * no total across cases.
 */
export function CvmPartiesPage() {
  const { t, i18n } = useTranslation(['cvm', 'common']);
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('q') ?? '');
  const [q, setQ] = useState<string | null>(seed.get('q') || null);
  const [entityType, setEntityType] = useState(seed.get('entity_type') ?? '');
  const filters = { q, entity_type: entityType || null };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['cvm', 'parties', filters],
    queryFn: ({ pageParam }) =>
      fetchCvmParties({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cvm:parties.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cvm:parties.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cvm:parties.explainer" />
      {data ? <AsOfStrip asOf={data.as_of} /> : null}
      {data ? <PersonScopeCard answer={data.person_scope} /> : null}

      {data ? (
        <section className="rounded-lg border bg-card p-4" data-reach>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('cvm:parties.reach.title')}
          </h2>
          <Answer answer={data.reach} help={t('cvm:parties.reach.help')}>
            {(r) => (
              <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
                <Figure label={t('cvm:parties.reach.parties')} value={number.format(r.parties)} />
                <Figure label={t('cvm:parties.reach.persons')} value={number.format(r.persons)} />
                <Figure
                  label={t('cvm:parties.reach.companies')}
                  value={number.format(r.companies)}
                />
                <Figure label={t('cvm:parties.reach.unknown')} value={number.format(r.unknown)} />
                <Figure
                  label={t('cvm:parties.reach.withJudgment')}
                  value={number.format(r.with_judgment)}
                />
                <Figure
                  label={t('cvm:parties.reach.withSettlement')}
                  value={number.format(r.with_settlement)}
                />
                <Figure
                  label={t('cvm:parties.reach.withAppeal')}
                  value={number.format(r.with_appeal)}
                />
                <Figure
                  label={t('cvm:parties.reach.withRegister')}
                  value={number.format(r.with_register_events)}
                />
              </dl>
            )}
          </Answer>
        </section>
      ) : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQ(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="cvm-party-query" className="text-xs font-medium text-foreground">
            {t('cvm:parties.search.label')}
          </label>
          <input
            id="cvm-party-query"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('cvm:parties.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="cvm-entity-type" className="block text-xs font-medium text-foreground">
            {t('cvm:parties.filters.entityType')}
          </label>
          <select
            id="cvm-entity-type"
            value={entityType}
            onChange={(event) => setEntityType(event.target.value)}
            className="rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('cvm:parties.filters.any')}</option>
            {ENTITY_TYPES.map((k) => (
              <option key={k} value={k}>
                {t(`cvm:entityTypes.${k}`)}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          {t('cvm:parties.search.submit')}
        </button>
        <div className="ml-auto">
          <ExportButton path={`/api/v1/cvm/export/parties?${cvmPartyParams(filters).toString()}`} />
        </div>
      </form>

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : !data ? (
        <SkeletonLoader rows={8} />
      ) : data.total === 0 ? (
        <EmptyState hint={t('cvm:list.empty')} />
      ) : (
        <>
          <p
            className="text-xs text-muted-foreground"
            aria-live="polite"
            data-total={data.total}
            data-stale={isPlaceholderData ? 'true' : 'false'}
          >
            {t('cvm:list.showing', {
              loaded: number.format(items.length),
              total: number.format(data.total),
            })}
          </p>
          <div
            ref={scrollRef}
            className={TABLE_REGION}
            role="region"
            aria-label={t('cvm:parties.tableLabel')}
          >
            <table className="w-full text-sm">
              <thead className={THEAD}>
                <tr>
                  <th className={TH}>{t('cvm:parties.columns.name')}</th>
                  <th className={TH}>{t('cvm:parties.columns.kind')}</th>
                  <th className={TH_NUM}>{t('cvm:parties.columns.judgments')}</th>
                  <th className={TH_NUM}>{t('cvm:parties.columns.sanctionRows')}</th>
                  <th className={TH_NUM}>{t('cvm:parties.columns.settlements')}</th>
                  <th className={TH_NUM}>{t('cvm:parties.columns.appeals')}</th>
                  <th className={TH_NUM}>{t('cvm:parties.columns.registerEvents')}</th>
                  <th className={TH}>{t('cvm:parties.columns.span')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <PartyRow key={row.slug} row={row} />
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} aria-hidden className="h-px" />
            <LoadMore
              hasNextPage={Boolean(hasNextPage)}
              isFetchingNextPage={isFetchingNextPage}
              loadMore={loadMore}
              total={data.total}
              pageSize={PAGE_SIZE}
            />
          </div>
        </>
      )}

      <FieldGuide i18nKey="cvm:parties.guide" />
    </div>
  );
}

function PartyRow({ row }: { row: CvmDirectoryRow }) {
  const { t, i18n } = useTranslation(['cvm']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <tr className="border-t hover:bg-muted/30">
      <td className={TD}>
        <Link
          to={`/cvm/parties/${encodeURIComponent(row.slug)}`}
          className="text-primary underline-offset-2 hover:underline"
        >
          {row.name}
        </Link>
      </td>
      <td className={TD_MUTED}>
        {t(`cvm:entityTypes.${row.entity_type}`, {
          defaultValue: row.entity_type,
        })}
      </td>
      <td className={TD_NUM}>{number.format(row.judgments)}</td>
      <td className={TD_NUM}>{number.format(row.sanction_rows)}</td>
      <td className={TD_NUM}>{number.format(row.settlements)}</td>
      <td className={TD_NUM}>{number.format(row.appeals)}</td>
      <td className={TD_NUM}>{number.format(row.register_events)}</td>
      <td className={TD_MUTED}>
        {row.first_date ?? '—'}
        {row.last_date && row.last_date !== row.first_date ? ` – ${row.last_date}` : ''}
      </td>
    </tr>
  );
}
